/**
 * Auth Interceptor
 *
 * Functional HTTP interceptor that attaches Bearer tokens to outgoing requests.
 * Uses the TokenProviderService to obtain tokens via the Custom Event pattern.
 *
 * On a 401 response the interceptor clears the cached token, requests a fresh
 * one from the host (with refresh:true), and retries the request once.
 * A second 401 or any 403 is propagated unchanged.
 *
 * The user's Auth0 token is passed to fga-proxy for user identification/audit.
 * The fga-proxy uses its own FGA client credentials for FGA API calls.
 *
 * @packageDocumentation
 */

import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { TokenProviderService } from '../services/token-provider.service';
import { LoggerService } from '../services/logger.service';

/**
 * Clones a request with a new Bearer token.
 */
function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * HTTP interceptor function that adds Authorization header to requests
 * and retries once with a fresh token on 401 responses.
 *
 * @example
 * ```typescript
 * // In elements.ts or app config:
 * provideHttpClient(withInterceptors([authInterceptor]))
 * ```
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  // Skip if Authorization header already present
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  const tokenProvider = inject(TokenProviderService);
  const logger = inject(LoggerService);

  return from(
    tokenProvider.getToken().catch(err => {
      logger.error('AuthInterceptor', 'Token retrieval failed', { error: err.message });
      throw new Error('Authentication failed: Unable to obtain token');
    })
  ).pipe(
    switchMap(token =>
      next(withBearer(req, token)).pipe(
        catchError((error: unknown) => {
          // Only retry on 401 (expired/invalid token), not 403 (forbidden)
          if (error instanceof HttpErrorResponse && error.status === 401) {
            logger.warn('AuthInterceptor', 'Received 401 — requesting token refresh');

            return from(
              tokenProvider.refreshToken().catch(refreshErr => {
                logger.error('AuthInterceptor', 'Token refresh failed', { error: refreshErr.message });
                throw new Error('Authentication failed: Unable to refresh token');
              })
            ).pipe(
              switchMap(freshToken => next(withBearer(req, freshToken)))
            );
          }
          return throwError(() => error);
        })
      )
    )
  );
};
